import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import PriceDisplay from "./PriceDisplay";
import { ProductAppPriceRow } from "./ProductAppPricePanel";
import { shouldShowAppPriceIcon } from "../utils/productAppPrice";
import "./ProductPriceWithApps.css";
import "./product.css";

const HIDE_DELAY_MS = 150;

export default function ProductPriceWithApps({
  product,
  deliveryApps = [],
  value,
  iconSize = 14,
  color = "",
  className = "",
}) {
  const { t } = useTranslation("translation");
  const [visible, setVisible] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const bubbleRef = useRef(null);
  const hideTimerRef = useRef(null);

  const showIcon = shouldShowAppPriceIcon(product, deliveryApps);
  const displayValue = value != null && value !== "" ? value : product?.price ?? 0;

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current != null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const estimatedWidth = 160;
    const pad = 8;
    let left = rect.left + rect.width / 2;
    const minLeft = estimatedWidth / 2 + pad;
    const maxLeft = window.innerWidth - estimatedWidth / 2 - pad;
    left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft));

    const rowCount = Math.max(deliveryApps.length, 1);
    const estimatedHeight = 12 + rowCount * 22;
    let top = rect.bottom + gap;
    if (top + estimatedHeight > window.innerHeight - pad) {
      top = Math.max(pad, rect.top - gap - estimatedHeight);
    }
    setCoords({ top, left });
  }, [deliveryApps.length]);

  const show = useCallback(() => {
    clearHideTimer();
    updatePosition();
    setVisible(true);
  }, [clearHideTimer, updatePosition]);

  const scheduleHide = useCallback(() => {
    if (pinned) return;
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      setVisible(false);
      hideTimerRef.current = null;
    }, HIDE_DELAY_MS);
  }, [clearHideTimer, pinned]);

  const togglePin = (event) => {
    event.preventDefault();
    event.stopPropagation();
    clearHideTimer();
    if (pinned) {
      setPinned(false);
      setVisible(false);
      return;
    }
    updatePosition();
    setPinned(true);
    setVisible(true);
  };

  useEffect(() => () => clearHideTimer(), [clearHideTimer]);

  useEffect(() => {
    if (!visible) return undefined;
    const onReposition = () => updatePosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [visible, updatePosition]);

  useEffect(() => {
    if (!pinned) return undefined;
    const onDocDown = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (bubbleRef.current?.contains(event.target)) return;
      setPinned(false);
      setVisible(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        setPinned(false);
        setVisible(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pinned]);

  const bubble =
    showIcon &&
    visible &&
    createPortal(
      <div
        ref={bubbleRef}
        className="oh-app-price-popover"
        style={{ top: coords.top, left: coords.left }}
        role="tooltip"
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
      >
        {deliveryApps.map((app) => (
          <ProductAppPriceRow
            key={app.key}
            product={product}
            app={app}
            variant="popover"
          />
        ))}
      </div>,
      document.body
    );

  return (
    <span
      className={["oh-product-price-with-apps", className].filter(Boolean).join(" ")}
    >
      <PriceDisplay value={displayValue} iconSize={iconSize} color={color} />
      {showIcon ? (
        <button
          ref={triggerRef}
          type="button"
          className="oh-app-price-trigger"
          onMouseEnter={show}
          onMouseLeave={scheduleHide}
          onFocus={show}
          onBlur={scheduleHide}
          onClick={togglePin}
          aria-label={t("appPrices")}
          aria-expanded={visible}
        >
          <img
            src="/images/information-circle.svg"
            alt=""
            width={14}
            height={14}
            aria-hidden="true"
          />
        </button>
      ) : null}
      {bubble}
    </span>
  );
}
